import React, { useMemo, useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import InfoSvg from "../../../../common/svgs/InfoSvg";
import CustomTooltip from "../common/CustomTooltip";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import SecondaryButton from "../../../../common/ui/SecondaryButton";
import { useAuth } from "../../../../providers/AuthProvider";
import ScrollableWrapper from "../common/ScrollableWrapper";
import CustomSelect from "../common/CustomSelect";
import NumericInputField from "../common/NumericInputField";
import NumericInputFieldScale from "../common/NumericInputFieldScale";
import {
  convertHeightToMetric,
  convertWeightToDisplay,
  convertWeightToMetric,
  getHeightUnit,
  getWeightUnit,
} from "../logicBuilder/measurementUnitHelpers";
import {
  cmToInchesPrecise,
  ftAndInToInchesPrecise,
  inchesToFtAndInPrecise,
  kgToLbsPrecise,
} from "./pnoeUnitDisplay";
import {
  MAX_AGE,
  MAX_HEIGHT_CM,
  MAX_HEIGHT_FT,
  MAX_HEIGHT_IN,
  MAX_WEIGHT_KG,
  MAX_WEIGHT_LB,
  MIN_HEIGHT_CM,
  MIN_HEIGHT_FT,
  MIN_WEIGHT_KG,
  MIN_WEIGHT_LB,
} from "../../../questionnaire/constant";
import {
  PnoeLifestyleParameter,
  PnoeMeasurementType,
  PnoeParticipant,
  PnoeProtocol,
  toNumber,
} from "../../entities/pnoeMeasurement";
import { PnoeParameterOptions } from "./usePnoeParameters";
import { RMR_PROTOCOL_FIELDS } from "./PnoeProtocolSection";
import {
  foldLifestyleDraft,
  lifestyleDraftKey,
  PnoeEditBody,
  PnoeEditDraft,
  selectOptionsWithCurrent,
} from "./pnoeLifestyle";

/**
 * The side-panel body for editing one measurement's client details or its RMR
 * protocol (Figma node 16905:69485).
 *
 * Both patches accept the same shape of work — a handful of fields, a Cancel
 * and a Save — so one body serves both, keyed by `kind`. The panel shell
 * around it is `common/SidePanel`; the confirm prompt after Save is the
 * platform's two-action snackbar, raised by the caller.
 *
 * Only the fields the breath service accepts are here. `customer_name` is not
 * editable: the design's "Client" section does not show it, and changing it is
 * the only participant change that does *not* trigger a recalculation, so it
 * does not belong behind this prompt.
 *
 * Values are held in a local draft and only handed up on Save, so Cancel and
 * the X both discard cleanly without touching the loaded measurement.
 */
export type PnoeEditKind = "participant" | "protocol";

/**
 * Participant fields, in the order the design lists them. `exercise_frequency`
 * is deliberately not here: the lifestyle questions replaced it, the service
 * stores null for it on new tests, and the review asked for it to go from
 * the page and the panel alike (a historical value is not worth showing).
 */
const PARTICIPANT_ROWS = [
  { key: "gender", label: "Gender", kind: "option" as const },
  { key: "age", label: "Age", kind: "number" as const, unit: "years" },
  { key: "weight_kg", label: "Weight", kind: "weight" as const },
  { key: "height_cm", label: "Height", kind: "height" as const },
  { key: "fvc_lt", label: "FVC", kind: "number" as const, unit: "L" },
  { key: "race", label: "Race", kind: "option" as const },
] as const;

const COPY: Record<PnoeEditKind, { title: string; subtitle: string }> = {
  participant: {
    title: "Edit client details",
    subtitle:
      "Applies to this measurement only. The client's profile stays unchanged.",
  },
  protocol: {
    title: "Edit protocol",
    subtitle:
      "Applies to this measurement only. The client's profile stays unchanged.",
  },
};

/**
 * The draft is a flat map of field key → value, as the PATCH bodies are —
 * except the lifestyle answers, which the body nests under
 * `lifestyle_answers`. In the draft they live under `lifestyle.<key>` and are
 * folded back on Save (`foldLifestyleDraft`), so one diff serves everything.
 */
export type { PnoeEditDraft, PnoeEditBody } from "./pnoeLifestyle";

interface PnoeEditPanelProps {
  kind: PnoeEditKind;
  participant: PnoeParticipant;
  protocol: PnoeProtocol;
  measurementType: PnoeMeasurementType;
  /** Option lists from the breath service's `configuration` endpoint. */
  participantOptions: PnoeParameterOptions;
  restingOptions: PnoeParameterOptions;
  /** field_name → help text, for the participant and protocol questions. */
  parameterDetails?: Record<string, string>;
  /** The lifestyle question set, in order. Empty hides the group. */
  lifestyleParameters?: PnoeLifestyleParameter[];
  onClose: () => void;
  /** Handed only the fields the user actually changed. */
  onSave: (changed: PnoeEditBody) => void;
  saving?: boolean;
}

const PnoeEditPanel: React.FC<PnoeEditPanelProps> = ({
  kind,
  participant,
  protocol,
  measurementType,
  participantOptions,
  restingOptions,
  parameterDetails = {},
  lifestyleParameters = [],
  onClose,
  onSave,
  saving = false,
}) => {
  const theme: Theme = useTheme();
  // 1 = metric, 2 = imperial. Set per affiliate in Settings.
  const { AuthUser } = useAuth();
  const unitType = AuthUser?.unitType;

  // The stored values this draft starts from, always in the API's own units.
  const initial: PnoeEditDraft = useMemo(() => {
    if (kind === "protocol") {
      return RMR_PROTOCOL_FIELDS.reduce<PnoeEditDraft>((acc, field) => {
        acc[field.key] = (protocol as any)[field.key] ?? null;
        return acc;
      }, {});
    }
    const draft = PARTICIPANT_ROWS.reduce<PnoeEditDraft>((acc, row) => {
      acc[row.key] = (participant as any)[row.key] ?? null;
      return acc;
    }, {});
    // Only the questions the service asks today: it accepts only current,
    // enabled choices, so a snapshot-only answer is shown on the page but
    // has no select here.
    lifestyleParameters.forEach((parameter) => {
      draft[lifestyleDraftKey(parameter.field_name)] =
        participant.lifestyle_answers?.[parameter.field_name]?.value ?? null;
    });
    return draft;
  }, [kind, participant, protocol, lifestyleParameters]);

  const [draft, setDraft] = useState<PnoeEditDraft>(initial);

  const set = (key: string, value: string | number | null) =>
    setDraft((previous) => ({ ...previous, [key]: value }));

  // Only send what moved: every participant field other than the name
  // triggers a report recalculation, so an unchanged field must not be in the
  // body at all. (The numeric tolerance below only applies where the stored
  // value parses as a number; choice values are enum-like strings, so for
  // them plain equality decides.)
  const changed = useMemo(() => {
    const body: PnoeEditDraft = {};
    Object.keys(initial).forEach((key) => {
      const before = initial[key];
      const after = draft[key];
      const same =
        before === after ||
        // Decimals arrive as strings ("67.00"), so compare numerically when
        // both sides parse — otherwise "67.00" vs 67 reads as a change.
        (toNumber(before as any) !== null &&
          toNumber(before as any) === toNumber(after as any));
      if (!same) body[key] = after;
    });
    return body;
  }, [draft, initial]);

  const hasChanges = Object.keys(changed).length > 0;

  const optionsFor = (key: string) =>
    (kind === "protocol" ? restingOptions : participantOptions)[key] ?? [];

  /**
   * One select. Disabled choices are left out, except a stored value that
   * points at one — that stays, flagged, so the field does not read as empty.
   * `hint` is the question's help text, behind an info icon inside the field
   * (the same treatment the detail page gives it): shown under the field it
   * pulled the eye from the question itself, which is set smaller.
   */
  const renderChoice = (
    key: string,
    label: string,
    answers: ReturnType<typeof optionsFor>,
    hint?: string,
    currentLabel?: string | null
  ) => {
    const current = (draft[key] as string) || null;
    return (
      <CustomSelect
        key={key}
        label={label}
        options={selectOptionsWithCurrent(answers, current, currentLabel)}
        value={current}
        onChange={(value) => set(key, value)}
        placeholder="Select option"
        // The panel is a drawer at z-index 1200, so the dropdown has to
        // clear it — CustomSelect's 1300 default does.
        endAdornment={
          hint ? (
            <CustomTooltip message={hint} customHeight="auto">
              <span style={styles.hintIcon} aria-label={hint}>
                <InfoSvg color={theme.colors_ds.NEUTRAL_ICON_WEAK} size={16} />
              </span>
            </CustomTooltip>
          ) : undefined
        }
      />
    );
  };

  const renderSelect = (key: string, label: string) =>
    renderChoice(key, label, optionsFor(key), parameterDetails[key]);

  const renderParticipantRow = (row: (typeof PARTICIPANT_ROWS)[number]) => {
    switch (row.kind) {
      case "option":
        return renderSelect(row.key, row.label);

      case "weight": {
        const kg = toNumber(draft[row.key] as any);
        const imperial = unitType === 2;
        // One decimal in both systems. The shared converter rounds lbs to a
        // whole number, so a typed 165.4 lbs came back as 165 — the PNOĒ
        // detail keeps the precision it stores (see pnoeUnitDisplay).
        const shown =
          kg === null
            ? null
            : imperial
            ? kgToLbsPrecise(kg)
            : Number(convertWeightToDisplay(kg, unitType).toFixed(1));
        return (
          <NumericInputFieldScale
            key={row.key}
            label={row.label}
            unit={getWeightUnit(unitType)}
            value={shown}
            min={imperial ? MIN_WEIGHT_LB : MIN_WEIGHT_KG}
            max={imperial ? MAX_WEIGHT_LB : MAX_WEIGHT_KG}
            decimals={1}
            name="weight"
            acceptCommaDecimal
            // Storage is always kg, whatever the affiliate types in.
            onChange={(value) =>
              set(
                row.key,
                value === null ? null : convertWeightToMetric(value, unitType)
              )
            }
          />
        );
      }

      case "height": {
        const cm = toNumber(draft[row.key] as any);

        // Imperial height is a ft + in pair. `HeightInputImperial` renders
        // exactly that, but its two boxes are internal, so arrows cannot be
        // put on them from outside — and it also serves the Logic Builder,
        // which did not ask for a stepper. So the pair is composed here, with
        // the PNOĒ-local precise helpers (the shared `inToFtAndIn` /
        // `ftAndInToIn` round to whole inches, so 9.5 in came back as 10),
        // and each box gets its own arrows.
        if (unitType === 2) {
          const totalInches = cm === null ? null : cmToInchesPrecise(cm);
          const { feet, inches } = inchesToFtAndInPrecise(totalInches ?? 0);

          const setFromPair = (ft: number | null, inch: number | null) => {
            if (ft === null && inch === null) return set(row.key, null);
            // Stored as one total, so a carry needs no special case: 5 ft
            // 12 in is 72 in, which redisplays as 6 ft 0 in.
            const total = Math.max(
              0,
              ftAndInToInchesPrecise(ft ?? 0, inch ?? 0)
            );
            set(row.key, convertHeightToMetric(total, unitType));
          };

          return (
            <div key={row.key} style={styles.pair}>
              <NumericInputFieldScale
                label={`${row.label} (ft)`}
                unit="ft"
                value={totalInches === null ? null : feet}
                min={MIN_HEIGHT_FT}
                max={MAX_HEIGHT_FT}
                name="height-ft"
                onChange={(ft) => setFromPair(ft, inches)}
              />
              {/* One past each end, so the arrows carry into feet instead of
                  stopping at 11 in — `setFromPair` normalises the total and
                  the box redisplays inside 0–11, so neither arrow ever sits
                  at its bound. */}
              <NumericInputFieldScale
                label={`${row.label} (in)`}
                unit="in"
                value={totalInches === null ? null : inches}
                min={-1}
                max={MAX_HEIGHT_IN + 1}
                decimals={1}
                name="height-in"
                acceptCommaDecimal
                onChange={(inch) => setFromPair(feet, inch)}
              />
            </div>
          );
        }

        return (
          <NumericInputFieldScale
            key={row.key}
            label={row.label}
            unit={getHeightUnit(unitType)}
            value={cm === null ? null : Math.round(cm)}
            min={MIN_HEIGHT_CM}
            max={MAX_HEIGHT_CM}
            name="height"
            onChange={(value) => set(row.key, value)}
          />
        );
      }

      case "number":
      default: {
        const value = draft[row.key];

        if (row.key === "age") {
          return (
            <NumericInputFieldScale
              key={row.key}
              label={row.label}
              unit={row.unit}
              value={toNumber(value as any)}
              min={0}
              max={MAX_AGE}
              name="age"
              onChange={(next) => set(row.key, next)}
            />
          );
        }

        // FVC is a free decimal (2.9221) with no meaningful step, so it keeps
        // the plain field.
        return (
          <NumericInputField
            key={row.key}
            label={row.label}
            unit={row.unit}
            value={value === null || value === undefined ? "" : String(value)}
            allowNegative={false}
            acceptCommaDecimal
            name={row.key}
            handleChange={(event) => {
              const raw = event.target.value;
              set(row.key, raw === "" ? null : Number(raw));
            }}
          />
        );
      }
    }
  };

  const copy = COPY[kind];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerText}>
          <div style={styles.title(theme)}>{copy.title}</div>
          <div style={styles.subtitle(theme)}>{copy.subtitle}</div>
        </div>
        <div style={styles.iconWrapper} onClick={onClose} role="button">
          <XIconSvg />
        </div>
      </div>

      <div style={styles.body}>
        <ScrollableWrapper>
          <div style={styles.fields}>
            {kind === "participant"
              ? PARTICIPANT_ROWS.map(renderParticipantRow)
              : RMR_PROTOCOL_FIELDS.map((field) =>
                  renderSelect(field.key, field.label)
                )}

            {/* The lifestyle questions continue the client fields, as they
                do on the page. */}
            {kind === "participant" &&
              lifestyleParameters.map((parameter) =>
                renderChoice(
                  lifestyleDraftKey(parameter.field_name),
                  parameter.question,
                  parameter.answers,
                  parameter.details,
                  participant.lifestyle_answers?.[parameter.field_name]?.label
                )
              )}
            <div style={{ height: "24px" }} />
          </div>
        </ScrollableWrapper>
      </div>

      <div style={styles.footer}>
        <SecondaryButton label="Cancel" onClick={onClose} />
        <PrimaryButton
          label="Save"
          onClick={() => onSave(foldLifestyleDraft(changed))}
          isDisable={!hasChanges || saving}
        />
      </div>
    </div>
  );
};

export default PnoeEditPanel;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    height: "100%",
    width: "100%",
    padding: "16px",
    boxSizing: "border-box" as "border-box",
    gap: "32px",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
    flexShrink: 0,
  },
  headerText: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    flex: 1,
    minWidth: 0,
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    lineHeight: "1.4",
    letterSpacing: "-0.4px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  subtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.4",
    letterSpacing: "-0.24px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  iconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px",
    borderRadius: "4px",
    cursor: "pointer",
    flexShrink: 0,
  },
  body: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden" as "hidden",
  },
  fields: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  // The imperial height's ft and in boxes, side by side.
  pair: {
    display: "flex",
    gap: "16px",
  },
  // `CustomTooltip` wraps its child in a block; the icon box keeps it sized
  // to the glyph inside the select's adornment slot.
  hintIcon: {
    display: "inline-flex",
    alignItems: "center",
    cursor: "default",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "16px",
    flexShrink: 0,
  },
};
